/**
 * Wisdom Pillars — the wisdom_categories editor.
 *
 * A DataGrid of every pillar in sort_order with its real colour swatches, a
 * create/edit dialog (emoji icon + two hex colour pickers, validated the same
 * way the service validates: #RRGGBB and a lowercase-hyphen slug), up/down
 * reordering that commits through PUT /categories/reorder, and a soft-delete
 * confirmation (the service only ever sets is_active=false — member entries
 * reference categories).
 *
 * Writes 403 for view_only admins; that surfaces as a snackbar, never a crash.
 */

import React from 'react';
import {
  Box,
  Button,
  Alert,
  Chip,
  Stack,
  TextField,
  CircularProgress,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Typography,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CategoryIcon from '@mui/icons-material/Category';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import EmptyState from '../../common/EmptyState';
import { brandColors } from '../../../theme/brandColors';
import {
  lifeAdviceApi,
  WisdomCategory,
  CategoryInput,
} from '../../../services/lifeAdviceAdminService';
import {
  PageHeader,
  ColorSwatch,
  errorMessage,
  writeErrorMessage,
  isBackendDown,
} from '../common/lifeAdviceUi';
import LifeAdviceActionConfirm from '../common/LifeAdviceActionConfirm';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,48}[a-z0-9]$/;

interface DraftState {
  slug: string;
  name: string;
  icon: string;
  color: string;
  light_bg: string;
  description: string;
  is_active: boolean;
}

const BLANK_DRAFT: DraftState = {
  slug: '',
  name: '',
  icon: '💡',
  color: brandColors.deepTeal,
  light_bg: brandColors.softCream,
  description: '',
  is_active: true,
};

function toDraft(c: WisdomCategory): DraftState {
  return {
    slug: c.slug,
    name: c.name,
    icon: c.icon,
    color: c.color,
    light_bg: c.light_bg,
    description: c.description || '',
    is_active: c.is_active,
  };
}

const WisdomPillarsPage: React.FC = () => {
  const [rows, setRows] = React.useState<WisdomCategory[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [down, setDown] = React.useState(false);
  const [toast, setToast] = React.useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<WisdomCategory | null>(null);
  const [draft, setDraft] = React.useState<DraftState>(BLANK_DRAFT);
  const [saving, setSaving] = React.useState(false);
  const [dialogError, setDialogError] = React.useState<string | null>(null);

  const [confirming, setConfirming] = React.useState<WisdomCategory | null>(null);
  const [confirmBusy, setConfirmBusy] = React.useState(false);
  const [reordering, setReordering] = React.useState(false);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setDown(false);
    try {
      setRows(await lifeAdviceApi.categories.list());
    } catch (e) {
      setDown(isBackendDown(e));
      setError(errorMessage(e, 'Failed to load wisdom pillars.'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const openNew = () => {
    setEditing(null);
    setDraft({ ...BLANK_DRAFT });
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEdit = (c: WisdomCategory) => {
    setEditing(c);
    setDraft(toDraft(c));
    setDialogError(null);
    setDialogOpen(true);
  };

  const draftInvalid = (): string | null => {
    if (!SLUG_RE.test(draft.slug)) {
      return 'Slug must be lowercase letters, numbers and hyphens (no leading/trailing hyphen).';
    }
    if (!draft.name.trim()) return 'Name is required.';
    if (!draft.icon.trim()) return 'Icon is required (an emoji works best).';
    if (!HEX_RE.test(draft.color)) return 'Colour must be a #RRGGBB hex value.';
    if (!HEX_RE.test(draft.light_bg)) return 'Light background must be a #RRGGBB hex value.';
    return null;
  };

  const save = async () => {
    const invalid = draftInvalid();
    if (invalid) {
      setDialogError(invalid);
      return;
    }
    setSaving(true);
    setDialogError(null);
    const payload: CategoryInput = {
      slug: draft.slug,
      name: draft.name,
      icon: draft.icon,
      color: draft.color,
      light_bg: draft.light_bg,
      description: draft.description || null,
      is_active: draft.is_active,
    };
    try {
      if (editing) {
        await lifeAdviceApi.categories.update(editing.id, payload);
        setToast(`Saved "${draft.name}"`);
      } else {
        await lifeAdviceApi.categories.create({
          ...payload,
          sort_order: rows.length + 1,
        });
        setToast(`Created "${draft.name}"`);
      }
      setDialogOpen(false);
      reload();
    } catch (e) {
      setDialogError(writeErrorMessage(e, 'Could not save this pillar.'));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    if (!confirming) return;
    setConfirmBusy(true);
    try {
      await lifeAdviceApi.categories.deactivate(confirming.id);
      setToast(`"${confirming.name}" is no longer offered to members`);
      setConfirming(null);
      reload();
    } catch (e) {
      setToast(writeErrorMessage(e, 'Could not deactivate this pillar.'));
      setConfirming(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  /** Swap a row with its neighbour and commit the whole order in one PUT. */
  const move = async (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    setRows(next);
    setReordering(true);
    try {
      const updated = await lifeAdviceApi.categories.reorder(
        next.map((r, i) => ({ id: r.id, sort_order: i + 1 }))
      );
      if (updated.length > 0) setRows(updated);
      setToast('Pillar order saved');
    } catch (e) {
      setToast(writeErrorMessage(e, 'Could not save the new order.'));
      reload();
    } finally {
      setReordering(false);
    }
  };

  const columns: GridColDef<WisdomCategory>[] = [
    {
      field: 'icon',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<WisdomCategory, string>) => (
        <span style={{ fontSize: 20 }} aria-hidden="true">
          {params.value}
        </span>
      ),
    },
    { field: 'name', headerName: 'Pillar', flex: 1, minWidth: 200 },
    {
      field: 'slug',
      headerName: 'Slug',
      width: 150,
      renderCell: (params: GridRenderCellParams<WisdomCategory, string>) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'color',
      headerName: 'Colours',
      width: 170,
      sortable: false,
      renderCell: (params: GridRenderCellParams<WisdomCategory>) => (
        <ColorSwatch
          color={params.row.color}
          lightBg={params.row.light_bg}
          label={params.row.name}
        />
      ),
    },
    {
      field: 'total_prompts',
      headerName: 'Prompts',
      width: 100,
      type: 'number',
      valueGetter: (_value, row) => row.total_prompts ?? row.prompt_count ?? 0,
    },
    {
      field: 'total_curated',
      headerName: 'Curated',
      width: 100,
      type: 'number',
      valueGetter: (_value, row) => row.total_curated ?? 0,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 110,
      renderCell: (params: GridRenderCellParams<WisdomCategory, boolean>) =>
        params.value ? (
          <Chip size="small" label="Active" color="success" variant="outlined" />
        ) : (
          <Chip size="small" label="Hidden" variant="outlined" />
        ),
    },
    {
      field: 'sort_order',
      headerName: 'Order',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<WisdomCategory>) => {
        const index = rows.findIndex((r) => r.id === params.row.id);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="small"
              aria-label={`Move ${params.row.name} up`}
              disabled={index <= 0 || reordering}
              onClick={(e) => {
                e.stopPropagation();
                move(index, -1);
              }}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label={`Move ${params.row.name} down`}
              disabled={index < 0 || index >= rows.length - 1 || reordering}
              onClick={(e) => {
                e.stopPropagation();
                move(index, 1);
              }}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<WisdomCategory>) => (
        <Box>
          <IconButton
            size="small"
            aria-label={`Edit ${params.row.name}`}
            onClick={(e) => {
              e.stopPropagation();
              openEdit(params.row);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Deactivate ${params.row.name}`}
            disabled={!params.row.is_active}
            onClick={(e) => {
              e.stopPropagation();
              setConfirming(params.row);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (down) {
    return (
      <EmptyState
        icon="error"
        title="LifeAdvice service is unreachable"
        description={`The admin API could not reach lifescribe-advice. ${error || ''}`}
        action={{ label: 'Retry', onClick: reload, variant: 'contained' }}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={<CategoryIcon sx={{ fontSize: 32, color: brandColors.deepTeal }} />}
        title="Wisdom Pillars"
        subtitle="The categories members choose from. Colour, icon, and order are exactly what the app renders."
        helpId="lifeadvice-pillars"
        actions={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={reload} disabled={loading}>
              Refresh
            </Button>
            <Button startIcon={<AddIcon />} variant="contained" onClick={openNew}>
              New pillar
            </Button>
          </Stack>
        }
      />

      {error && !down && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!loading && rows.length === 0 && !error ? (
        <EmptyState
          icon="empty_box"
          title="No wisdom pillars yet"
          description="Pillars are the categories members browse. Create the first one to get LifeAdvice started."
          action={{ label: 'New pillar', onClick: openNew, variant: 'contained' }}
        />
      ) : (
        <Box sx={{ height: 620, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            loading={loading || reordering}
            disableRowSelectionOnClick
            onRowClick={(params) => openEdit(params.row as WisdomCategory)}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>
      )}

      {/* ── Create / edit dialog ─────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit "${editing.name}"` : 'New wisdom pillar'}</DialogTitle>
        <DialogContent dividers>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              fullWidth
              autoFocus
            />
            <TextField
              label="Slug"
              value={draft.slug}
              onChange={(e) => setDraft({ ...draft, slug: e.target.value.toLowerCase() })}
              helperText="Lowercase letters, numbers and hyphens. Used by the app and CSV imports."
              fullWidth
            />
            <TextField
              label="Icon"
              value={draft.icon}
              onChange={(e) => setDraft({ ...draft, icon: e.target.value })}
              helperText="A single emoji — this is what members see on the pillar tile."
              inputProps={{ maxLength: 10 }}
              sx={{ maxWidth: 220 }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Colour"
                value={draft.color}
                onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <input
                        type="color"
                        aria-label="Pick pillar colour"
                        value={HEX_RE.test(draft.color) ? draft.color : '#1A5F7A'}
                        onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                        style={{ width: 32, height: 24, border: 'none', background: 'none' }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Light background"
                value={draft.light_bg}
                onChange={(e) => setDraft({ ...draft, light_bg: e.target.value })}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <input
                        type="color"
                        aria-label="Pick pillar light background"
                        value={HEX_RE.test(draft.light_bg) ? draft.light_bg : '#F9F3E9'}
                        onChange={(e) => setDraft({ ...draft, light_bg: e.target.value })}
                        style={{ width: 32, height: 24, border: 'none', background: 'none' }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <TextField
              label="Description"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              multiline
              minRows={2}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.is_active}
                  onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                />
              }
              label="Offered to members"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save pillar'}
          </Button>
        </DialogActions>
      </Dialog>

      <LifeAdviceActionConfirm
        open={!!confirming}
        destructive
        loading={confirmBusy}
        title="Stop offering this pillar?"
        message={
          confirming
            ? `"${confirming.name}" will be hidden from members immediately.`
            : ''
        }
        detail={
          confirming ? (
            <Typography variant="body2" color="text.secondary">
              {confirming.total_prompts ?? confirming.prompt_count ?? 0} prompts and{' '}
              {confirming.total_curated ?? 0} curated pieces sit under it.
            </Typography>
          ) : undefined
        }
        caption="Nothing is deleted — this is a soft deactivate, and existing member entries keep their category. Re-activate any time from the edit dialog."
        confirmLabel="Deactivate pillar"
        pendingLabel="Deactivating…"
        onConfirm={deactivate}
        onCancel={() => setConfirming(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WisdomPillarsPage;
